// src/pages/CreateServicePage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { PageHeader, RowActions, Section, StatusTag, TabBar, tablePagination, useFlatPage } from "../components/ui";
import "./CreateServicePage.css";

/* antd */
import {
  Space,
  Typography,
  Button,
  Table,
  Select,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  Alert,
} from "antd";

/* icons */
import {
} from "@ant-design/icons";
import { IconInfo, IconPlus, IconRefresh } from "../components/ui/icons";
import notify from "../utils/notify";

const { Text } = Typography;

/* utils */
const asNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const CreateServicePage = () => {
  const { authData } = useAuth();
  const hotelId = authData?.hotelId;

  useFlatPage();

  const [activeTab, setActiveTab] = useState("services"); // 'services' | 'menu' | 'types'
  const [error, setError] = useState("");

  /* ------------ Services ------------- */
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState(null);

  const selectedService = useMemo(
    () => services.find((s) => s.serviceId === selectedServiceId) || null,
    [services, selectedServiceId]
  );

  // Service modal (create/edit)
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalSaving, setServiceModalSaving] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm] = Form.useForm();

  /* ------------ Menu Items ------------- */
  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Item modal (create/edit)
  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalSaving, setItemModalSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm] = Form.useForm();

  /* ------------ Service Types ------------- */
  const [types, setTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);

  // Type modal (create/edit)
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [typeModalSaving, setTypeModalSaving] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [typeForm] = Form.useForm();

  /* ------------ Info modal ------------- */
  const [infoOpen, setInfoOpen] = useState(false);

  const hasSelectedService = useMemo(() => !!selectedServiceId, [selectedServiceId]);

  /* ------------ Loaders ------------- */
  const loadServices = useCallback(async () => {
    if (!hotelId) return;
    setServicesLoading(true);
    try {
      const res = await axiosInstance.get(`/Services/by-hotel/${hotelId}`, {
        params: { hotelId },
        validateStatus: (s) => [200, 204, 404].includes(s),
      });
      const list = Array.isArray(res.data) ? res.data : [];
      setServices(list);
      if (!selectedServiceId && list.length) {
        setSelectedServiceId(list[0].serviceId);
      }
      setError("");
    } catch (e) {
      setServices([]);
      setError(e?.response?.data?.message || "Failed to load services.");
    } finally {
      setServicesLoading(false);
    }
  }, [hotelId, selectedServiceId]);

  const loadItems = useCallback(async (sid) => {
    if (!sid) {
      setItems([]);
      return;
    }
    setItemsLoading(true);
    try {
      const res = await axiosInstance.get(`/MenuItems/by-service/${sid}`, {
        params: { hotelId },
        validateStatus: (s) => [200, 204, 404].includes(s),
      });
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItems([]);
      notify.error(e?.response?.data?.message || "Failed to load menu items.");
    } finally {
      setItemsLoading(false);
    }
  }, [hotelId]);

  const loadTypes = useCallback(
    async (sid) => {
      if (!sid) {
        setTypes([]);
        return;
      }
      setTypesLoading(true);
      try {
        const res = await axiosInstance.get(`/RoomServiceTypes/by-service/${sid}`, {
          params: { hotelId },
          validateStatus: (s) => [200, 204, 404].includes(s),
        });
        setTypes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setTypes([]);
        notify.error(e?.response?.data?.message || "Failed to load service types.");
      } finally {
        setTypesLoading(false);
      }
    },
    [hotelId]
  );

  useEffect(() => {
    if (!hotelId) {
      setError("Hotel ID is missing from auth.");
      return;
    }
    loadServices();
  }, [hotelId, loadServices]);

  useEffect(() => {
    if (selectedServiceId) {
      loadItems(selectedServiceId);
      loadTypes(selectedServiceId);
    } else {
      setItems([]);
      setTypes([]);
    }
  }, [selectedServiceId, loadItems, loadTypes]);

  /* ------------ Service: modal handlers ------------- */
  const openNewService = () => {
    setEditingService(null);
    serviceForm.resetFields();
    serviceForm.setFieldsValue({ name: "", description: "", isActive: true });
    setServiceModalOpen(true);
  };

  const openEditService = (record) => {
    setEditingService(record);
    serviceForm.setFieldsValue({
      name: record.name || "",
      description: record.description || "",
      isActive: !!record.isActive,
    });
    setServiceModalOpen(true);
  };

  const saveService = async () => {
    if (!hotelId) return;
    try {
      const vals = await serviceForm.validateFields();
      setServiceModalSaving(true);

      const payload = {
        hotelId,
        name: vals.name,
        description: vals.description || "",
        isActive: !!vals.isActive,
      };

      if (editingService?.serviceId) {
        await axiosInstance.put(`/Services/${editingService.serviceId}`, payload, {
          params: { hotelId }
        });
        notify.success("Service updated.");
      } else {
        const res = await axiosInstance.post(`/Services`, payload, {
          params: { hotelId }
        });
        const created = res.data || {};
        setSelectedServiceId(created.serviceId || created.id);
        notify.success("Service created.");
      }

      setServiceModalOpen(false);
      setEditingService(null);
      await loadServices();
    } catch (e) {
      if (e?.errorFields) return;
      notify.error(e?.response?.data?.message || "Failed to save service.");
    } finally {
      setServiceModalSaving(false);
    }
  };

  const deleteService = async (record) => {
    try {
      await axiosInstance.delete(`/Services/${record.serviceId}`, {
        params: { hotelId }
      });
      notify.success("Service deleted.");
      if (selectedServiceId === record.serviceId) {
        const next = services.filter((s) => s.serviceId !== record.serviceId);
        setSelectedServiceId(next[0]?.serviceId ?? null);
      }
      await loadServices();
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to delete service.");
    }
  };

  /* ------------ Menu Item: modal handlers ------------- */
  const openNewItem = () => {
    if (!hasSelectedService) {
      notify.info("Please select a service first.");
      return;
    }
    setEditingItem(null);
    itemForm.resetFields();
    itemForm.setFieldsValue({
      serviceId: selectedServiceId,
      category: "",
      name: "",
      description: "",
      price: null,
      estimatedReadyTime: null,
      isActive: true,
    });
    setItemModalOpen(true);
  };

  const openEditItem = (record) => {
    setEditingItem(record);
    itemForm.setFieldsValue({
      serviceId: record.serviceId,
      category: record.category || "",
      name: record.name || "",
      description: record.description || "",
      price: asNum(record.price) || 0,
      estimatedReadyTime: record.estimatedReadyTime ?? null,
      isActive: !!record.isActive,
    });
    setItemModalOpen(true);
  };

  const saveItem = async () => {
    const vals = await itemForm.validateFields().catch((e) => e);
    if (vals?.errorFields) return;

    const svcId = Number(vals.serviceId || selectedServiceId);
    if (!svcId) {
      notify.error("Please pick a service.");
      return;
    }

    try {
      setItemModalSaving(true);
      const payload = {
        hotelId,
        serviceId: svcId,
        category: vals.category || null,
        name: vals.name,
        description: vals.description || null,
        price: asNum(vals.price),
        estimatedReadyTime:
          vals.estimatedReadyTime != null ? asNum(vals.estimatedReadyTime) : null,
        isActive: !!vals.isActive,
      };

      if (editingItem?.menuItemId) {
        await axiosInstance.put(`/MenuItems/${editingItem.menuItemId}`, payload, {
          params: { hotelId }
        });
        notify.success("Item updated.");
      } else {
        await axiosInstance.post(`/MenuItems`, payload, {
          params: { hotelId }
        });
        notify.success("Item added.");
      }

      setItemModalOpen(false);
      setEditingItem(null);
      if (svcId !== selectedServiceId) setSelectedServiceId(svcId);
      await loadItems(svcId);
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to save item.");
    } finally {
      setItemModalSaving(false);
    }
  };

  const deleteItem = async (record) => {
    try {
      await axiosInstance.delete(`/MenuItems/${record.menuItemId}`, {
        params: { hotelId }
      });
      notify.success("Item deleted.");
      await loadItems(selectedServiceId);
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to delete item.");
    }
  };

  /* ------------ Service Type: modal handlers ------------- */
  const openNewType = () => {
    if (!hasSelectedService) {
      notify.info("Please select a service first.");
      return;
    }
    setEditingType(null);
    typeForm.resetFields();
    typeForm.setFieldsValue({
      serviceId: selectedServiceId,
      name: "",
      category: "",
      description: "",
      estimatedMinutes: null,
      isActive: true,
    });
    setTypeModalOpen(true);
  };

  const openEditType = (record) => {
    setEditingType(record);
    typeForm.setFieldsValue({
      serviceId: record.serviceId,
      name: record.name || "",
      category: record.category || "",
      description: record.description || "",
      estimatedMinutes: record.estimatedMinutes ?? null,
      isActive: !!record.isActive,
    });
    setTypeModalOpen(true);
  };

  const saveType = async () => {
    const vals = await typeForm.validateFields().catch((e) => e);
    if (vals?.errorFields) return;

    const svcId = Number(vals.serviceId || selectedServiceId);
    if (!svcId) {
      notify.error("Please pick a service.");
      return;
    }

    try {
      setTypeModalSaving(true);
      const payload = {
        hotelId,
        serviceId: svcId,
        name: vals.name,
        category: vals.category || null,
        description: vals.description || null,
        estimatedMinutes:
          vals.estimatedMinutes != null ? asNum(vals.estimatedMinutes) : null,
        isActive: !!vals.isActive,
      };

      if (editingType?.roomServiceTypeId) {
        await axiosInstance.put(`/RoomServiceTypes/${editingType.roomServiceTypeId}`, payload, {
          params: { hotelId }
        });
        notify.success("Service type updated.");
      } else {
        await axiosInstance.post(`/RoomServiceTypes`, payload, {
          params: { hotelId }
        });
        notify.success("Service type added.");
      }

      setTypeModalOpen(false);
      setEditingType(null);
      if (svcId !== selectedServiceId) setSelectedServiceId(svcId);
      await loadTypes(svcId);
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to save service type.");
    } finally {
      setTypeModalSaving(false);
    }
  };

  const deleteType = async (record) => {
    try {
      await axiosInstance.delete(`/RoomServiceTypes/${record.roomServiceTypeId}`, {
        params: { hotelId }
      });
      notify.success("Service type deleted.");
      await loadTypes(selectedServiceId);
    } catch (e) {
      notify.error(e?.response?.data?.message || "Failed to delete service type.");
    }
  };

  /* ------------ Tables ------------- */
  const serviceColumns = [
    { 
      title: "Name", 
      dataIndex: "name",
      width: 160,
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      width: 200,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => <StatusTag status={v ? "available" : "checkedout"}>{v ? "Active" : "Inactive"}</StatusTag>,
    },
    {
      title: "",
      dataIndex: "_actions",
      width: 110,
      align: "right",
      render: (_, row) => (
        <RowActions
          actions={[
            { key: "edit", label: "Edit service", icon: "edit", onClick: () => openEditService(row) },
            {
              key: "delete",
              label: "Delete service",
              icon: "delete",
              danger: true,
              confirm: "Delete this service?",
              okText: "Delete",
              onClick: () => deleteService(row),
            },
          ]}
        />
      ),
    },
  ];

  const itemColumns = [
    { 
      title: "#", 
      width: 50, 
      render: (_v, _r, i) => <Text type="secondary">{i + 1}</Text> 
    },
    { 
      title: "Category", 
      dataIndex: "category",
      width: 120,
    },
    { 
      title: "Name", 
      dataIndex: "name",
      width: 140,
    },
    {
      title: "Price",
      dataIndex: "price",
      width: 90,
      render: (v) =>
        v != null
          ? `BWP ${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : "-",
    },
    {
      title: "ETA (min)",
      dataIndex: "estimatedReadyTime",
      width: 100,
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => <StatusTag status={v ? "available" : "checkedout"}>{v ? "Active" : "Inactive"}</StatusTag>,
    },
    {
      title: "",
      dataIndex: "_actions",
      width: 110,
      align: "right",
      render: (_, row) => (
        <RowActions
          actions={[
            { key: "edit", label: "Edit item", icon: "edit", onClick: () => openEditItem(row) },
            {
              key: "delete",
              label: "Delete item",
              icon: "delete",
              danger: true,
              confirm: "Delete this menu item?",
              okText: "Delete",
              onClick: () => deleteItem(row),
            },
          ]}
        />
      ),
    },
  ];

  const typeColumns = [
    { 
      title: "#", 
      width: 50, 
      render: (_v, _r, i) => <Text type="secondary">{i + 1}</Text> 
    },
    { 
      title: "Name", 
      dataIndex: "name",
      width: 140,
    },
    { 
      title: "Category", 
      dataIndex: "category", 
      width: 120, 
      render: (v) => v || "-" 
    },
    {
      title: "Description",
      dataIndex: "description",
      ellipsis: true,
      width: 160,
      render: (v) => v || <Text type="secondary">-</Text>,
    },
    {
      title: "Est. Minutes",
      dataIndex: "estimatedMinutes",
      width: 110,
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => <StatusTag status={v ? "available" : "checkedout"}>{v ? "Active" : "Inactive"}</StatusTag>,
    },
    {
      title: "",
      dataIndex: "_actions",
      width: 110,
      align: "right",
      render: (_, row) => (
        <RowActions
          actions={[
            { key: "edit", label: "Edit type", icon: "edit", onClick: () => openEditType(row) },
            {
              key: "delete",
              label: "Delete type",
              icon: "delete",
              danger: true,
              confirm: "Delete this service type?",
              okText: "Delete",
              onClick: () => deleteType(row),
            },
          ]}
        />
      ),
    },
  ];

  /* ------------ Render ------------- */
  if (!hotelId) {
    return (
      <div style={{ margin: "48px auto" }}>
        <Alert
          type="error"
          showIcon
          message="Hotel ID missing"
          description="Hotel ID was not found in auth data. Please sign in again."
        />
      </div>
    );
  }

  return (
    <div className="hms-page cs-page">
      <PageHeader
        actions={
          <>
            <Button icon={<IconInfo />} onClick={() => setInfoOpen(true)}>
              Guide
            </Button>
            <Button icon={<IconRefresh />} onClick={loadServices} loading={servicesLoading}>
              Refresh
            </Button>
          </>
        }
      />

        <div className="hms-card is-flush">
          <TabBar
            tabs={[
              { key: "services", label: "Services", count: services.length },
              { key: "menu", label: "Menu", count: items.length },
              { key: "types", label: "Service types", count: types.length },
            ]}
            value={activeTab}
            onChange={setActiveTab}
          />

          {error && <Alert type="error" showIcon message={error} style={{ margin: 16 }} />}

          <div>
            {/* SERVICES TAB */}
            {activeTab === "services" && (
              <div>
              <div className="hms-card-toolbar">
                <div className="cs-toolbar-left"><strong>Services</strong></div>
                <div className="hms-actions"><Space>
                    <Button 
                      type="primary" 
                      icon={<IconPlus />} 
                      onClick={openNewService}
                    >
                      New Service
                    </Button>
                  </Space></div>
              </div>
                <Table
                  className="hms-table"
                  rowKey="serviceId"
                  dataSource={services}
                  columns={serviceColumns}
                  loading={servicesLoading}
                  pagination={tablePagination("service")}
                  locale={{ emptyText: "No services yet." }}
                  scroll={{ x: 600 }}
                />
              </div>
            )}

            {/* MENU TAB */}
            {activeTab === "menu" && (
              <div>
              <div className="hms-card-toolbar">
                <div className="cs-toolbar-left"><Space wrap>
                    <span>Menu</span>
                    <Text type="secondary">for</Text>
                    <Select
                      placeholder={servicesLoading ? "Loading…" : "Select service"}
                      value={selectedServiceId ?? undefined}
                      onChange={(v) => setSelectedServiceId(v ?? null)}
                      loading={servicesLoading}
                      options={(services || []).map((s) => ({
                        value: s.serviceId,
                        label: s.name,
                      }))}
                      style={{ width: 200 }}
                    />
                  </Space></div>
                <div className="hms-actions"><Space>
                    <Button
                      icon={<IconRefresh />}
                      onClick={() => loadItems(selectedServiceId)}
                      disabled={!hasSelectedService}
                    >
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      icon={<IconPlus />}
                      onClick={openNewItem}
                      disabled={!hasSelectedService}
                    >
                      New Item
                    </Button>
                  </Space></div>
              </div>
                <Table
                  className="hms-table"
                  rowKey="menuItemId"
                  dataSource={items}
                  columns={itemColumns}
                  loading={itemsLoading}
                  pagination={tablePagination("item")}
                  locale={{
                    emptyText: hasSelectedService
                      ? "No menu items yet."
                      : "Select a service to view its menu.",
                  }}
                  scroll={{ x: 700 }}
                />
              </div>
            )}

            {/* SERVICE TYPES TAB */}
            {activeTab === "types" && (
              <div>
              <div className="hms-card-toolbar">
                <div className="cs-toolbar-left"><Space wrap>
                    <span>Service Types</span>
                    <Text type="secondary">for</Text>
                    <Select
                      placeholder={servicesLoading ? "Loading…" : "Select service"}
                      value={selectedServiceId ?? undefined}
                      onChange={(v) => setSelectedServiceId(v ?? null)}
                      loading={servicesLoading}
                      options={(services || []).map((s) => ({
                        value: s.serviceId,
                        label: s.name,
                      }))}
                      style={{ width: 200 }}
                    />
                  </Space></div>
                <div className="hms-actions"><Space>
                    <Button
                      icon={<IconRefresh />}
                      onClick={() => loadTypes(selectedServiceId)}
                      disabled={!hasSelectedService}
                    >
                      Refresh
                    </Button>
                    <Button
                      type="primary"
                      icon={<IconPlus />}
                      onClick={openNewType}
                      disabled={!hasSelectedService}
                    >
                      New Type
                    </Button>
                  </Space></div>
              </div>
                <Table
                  className="hms-table"
                  rowKey={(r) => r.roomServiceTypeId ?? r.id}
                  dataSource={types}
                  columns={typeColumns}
                  loading={typesLoading}
                  pagination={tablePagination("service type")}
                  locale={{
                    emptyText: hasSelectedService
                      ? "No service types yet."
                      : "Select a service to view types.",
                  }}
                  scroll={{ x: 700 }}
                />
              </div>
            )}
          </div>
        </div>

      {/* Service Modal */}
      <Modal
        open={serviceModalOpen}
        title={editingService ? "Edit service" : "New service"}
        onCancel={() => {
          setServiceModalOpen(false);
          setEditingService(null);
        }}
        onOk={saveService}
        okText={editingService ? "Save changes" : "Create service"}
        confirmLoading={serviceModalSaving}
        destroyOnHidden
        width={500}
      >
        <Form
          form={serviceForm}
          layout="vertical"
          initialValues={{ name: "", description: "", isActive: true }}
        >
          <Form.Item
            label="Service name"
            name="name"
            rules={[{ required: true, message: "Please enter service name" }]}
          >
            <Input placeholder="e.g., Housekeeping, Room Service, Spa, Laundry" />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} placeholder="Describe the service" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Menu Item Modal */}
      <Modal
        open={itemModalOpen}
        title={
          editingItem
            ? "Edit menu item"
            : `New menu item${selectedService ? ` — ${selectedService.name}` : ""}`
        }
        onCancel={() => {
          setItemModalOpen(false);
          setEditingItem(null);
        }}
        onOk={saveItem}
        okText={editingItem ? "Save changes" : "Add item"}
        confirmLoading={itemModalSaving}
        destroyOnHidden
        width={500}
      >
        <Form
          form={itemForm}
          layout="vertical"
          initialValues={{
            serviceId: selectedServiceId,
            category: "",
            name: "",
            description: "",
            price: null,
            estimatedReadyTime: null,
            isActive: true,
          }}
        >
          <Form.Item
            label="Service"
            name="serviceId"
            rules={[{ required: true, message: "Please choose a service" }]}
          >
            <Select
              placeholder="Select a service (e.g., Kitchen, Bar)"
              options={(services || []).map((s) => ({
                value: s.serviceId,
                label: s.name,
              }))}
            />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input placeholder="e.g., Food / Beverage" />
          </Form.Item>
          <Form.Item
            label="Item name"
            name="name"
            rules={[{ required: true, message: "Item name is required" }]}
          >
            <Input placeholder="Item name" />
          </Form.Item>
          <Form.Item
            label="Price"
            name="price"
            rules={[{ required: true, message: "Price is required" }]}
          >
            <InputNumber style={{ width: "100%" }} min={0} step={0.01} placeholder="0.00" />
          </Form.Item>
          <Form.Item label="Estimated ready time (min)" name="estimatedReadyTime">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>
          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Service Type Modal */}
      <Modal
        open={typeModalOpen}
        title={
          editingType
            ? "Edit service type"
            : `New service type${selectedService ? ` — ${selectedService.name}` : ""}`
        }
        onCancel={() => {
          setTypeModalOpen(false);
          setEditingType(null);
        }}
        onOk={saveType}
        okText={editingType ? "Save changes" : "Add service type"}
        confirmLoading={typeModalSaving}
        destroyOnHidden
        width={500}
      >
        <Form
          form={typeForm}
          layout="vertical"
          initialValues={{
            serviceId: selectedServiceId,
            name: "",
            category: "",
            description: "",
            estimatedMinutes: null,
            isActive: true,
          }}
        >
          <Form.Item
            label="Service"
            name="serviceId"
            rules={[{ required: true, message: "Please choose a service" }]}
          >
            <Select
              placeholder="Select a service"
              options={(services || []).map((s) => ({
                value: s.serviceId,
                label: s.name,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Type name"
            name="name"
            rules={[{ required: true, message: "Type name is required" }]}
          >
            <Input placeholder="e.g., Extra towels, Make up room" />
          </Form.Item>

          <Form.Item label="Category" name="category">
            <Input placeholder="e.g., Linen, Cleaning" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Optional description" />
          </Form.Item>

          <Form.Item label="Estimated minutes" name="estimatedMinutes">
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>

          <Form.Item label="Active" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Info / Help Modal */}
      <Modal
        open={infoOpen}
        onCancel={() => setInfoOpen(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setInfoOpen(false)}>
            Got it
          </Button>,
        ]}
        title="Services, menu and service types"
        width={600}
      >
        <p className="hms-modal-note">
          Manage your hotel's <strong>services</strong> (for example Housekeeping or Spa), their{" "}
          <strong>menu items</strong> (sellable items) and standard <strong>service types</strong> (common request
          templates such as "Extra towels").
        </p>

        <Section title="How it works">
          <ul style={{ paddingLeft: 20, margin: 0 }}>
            <li>
              <strong>Services:</strong> add and edit the parent services for your hotel.
            </li>
            <li>
              <strong>Menu:</strong> pick a service and manage its sellable items.
            </li>
            <li>
              <strong>Service types:</strong> define common request types, not tied to a room or booking.
            </li>
          </ul>
        </Section>
      </Modal>
    </div>
  );
};

export default CreateServicePage;